/**
 * The line under *Activity* on a phone (Aaron, Oct 27).
 *
 * The phone's page bar is `PageBar` — the same one the Contacts, Properties
 * and Jobs phone lists draw, so the title and the line under it are in the
 * same place at the same size (`PageBar`'s own `sub`, as `ListScreen` passes
 * it; never a second style). What the line *says* belongs to the tab that is
 * showing: the Calendar's day (*Thu, Oct 22 · 4 stops · about 52 min
 * driving*), the Tasks list's count (*12 tasks*), All activity's.
 *
 * So a tab says it with `useActivitySub(text)` and the page draws whatever was
 * said last. `null` says nothing, and so does a tab that has gone — its line
 * leaves with it, and a line from the tab before is never left behind. Two
 * speakers at once (a tab and something it shows) is allowed: the one that
 * started speaking most recently is drawn, and the other's line comes back
 * when it stops.
 *
 * Outside the Activity page the hook does nothing, so a component that says a
 * line can be drawn somewhere else without a provider.
 */
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from 'react'

type Say = (speaker: string, text: string | null) => void

interface Spoken { speaker: string; text: string }

export const ActivitySubContext = createContext<Say | null>(null)

/**
 * The page's half: what to pass to `PageBar sub`, and the `say` to put in
 * `ActivitySubContext` around the tabs.
 */
export function useActivitySubSlot(): { sub: string | null; say: Say } {
  const [lines, setLines] = useState<Spoken[]>([])
  const say = useCallback<Say>((speaker, text) => {
    setLines((current) => {
      const at = current.findIndex((line) => line.speaker === speaker)
      if (text === null) return at < 0 ? current : current.filter((line) => line.speaker !== speaker)
      if (at < 0) return [...current, { speaker, text }]
      if (current[at]?.text === text) return current
      const next = current.slice()
      next[at] = { speaker, text }
      return next
    })
  }, [])
  const sub = lines.length > 0 ? lines[lines.length - 1]?.text ?? null : null
  return useMemo(() => ({ sub, say }), [sub, say])
}

/** A tab's half: the line under *Activity* while this is mounted, `null` for none. */
export function useActivitySub(text: string | null): void {
  const say = useContext(ActivitySubContext)
  const speaker = useId()
  useEffect(() => {
    say?.(speaker, text)
  }, [say, speaker, text])
  useEffect(() => () => say?.(speaker, null), [say, speaker])
}
